import { isForeground, subscribeForeground } from './AppStateService';
import { POSITION_WATCH_OPTIONS } from './PositionSnapshotService';

// Multiplex the device subscription, not consent: each consumer explicitly
// subscribes/disposes under its own independent policy. Replay only a bounded,
// memory-only fix from this foreground device-watch generation.
const subscribers = new Map();
let nextId = 0, device = null, watchId = null, unsubscribe = null, generation = 0, latestFix = null, highAccuracy = false, fixOwners = new Set();
const stop = () => {
  generation++; latestFix = null; fixOwners.clear();
  if (watchId !== null) device?.clearWatch(watchId);
  watchId = null; device = null;
};
const reconcile = () => {
  if (!subscribers.size || !isForeground()) { stop(); return; }
  const requestedHighAccuracy = [...subscribers.values()].some(subscriber => subscriber.options.enableHighAccuracy === true);
  if (watchId !== null && highAccuracy !== requestedHighAccuracy) stop();
  if (watchId !== null || !navigator.geolocation?.watchPosition) return;
  highAccuracy = requestedHighAccuracy;
  device = navigator.geolocation;
  const epoch = ++generation;
  try {
    watchId = device.watchPosition(
      fix => {
        if (epoch !== generation || !isForeground()) return;
        latestFix = fix;
        fixOwners = new Set([...subscribers.values()].filter(subscriber => subscriber.options.enableHighAccuracy === true).map(subscriber => subscriber.options.owner_user_id).filter(Boolean));
        [...subscribers.values()].forEach(subscriber => subscriber.ok(fix));
      },
      error => {
        if (epoch !== generation) return;
        if (error?.code !== 3) latestFix = null;
        [...subscribers.values()].forEach(subscriber => subscriber.fail?.(error));
      },
      highAccuracy ? { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 } : POSITION_WATCH_OPTIONS
    );
  } catch (_) {
    watchId = null; device = null;
    [...subscribers.values()].forEach(subscriber => subscriber.fail?.({ code: 2 }));
  }
};
export const sharedGeolocation = {
  watchPosition(ok, fail, options = POSITION_WATCH_OPTIONS) {
    const id = ++nextId;
    subscribers.set(id, { ok, fail, options });
    if (!unsubscribe) unsubscribe = subscribeForeground(reconcile);
    reconcile();
    const age = Date.now() - latestFix?.timestamp;
    const canReplay = options.enableHighAccuracy !== true || (highAccuracy && fixOwners.has(options.owner_user_id));
    if (canReplay && latestFix && isForeground() && Number.isFinite(latestFix.timestamp) && age <= 60000 && age >= -5000) ok(latestFix);
    return id;
  },
  clearWatch(id) {
    const owner = subscribers.get(id)?.options.owner_user_id;
    subscribers.delete(id);
    if (owner && ![...subscribers.values()].some(subscriber => subscriber.options.owner_user_id === owner)) fixOwners.delete(owner);
    reconcile();
    if (!subscribers.size) { unsubscribe?.(); unsubscribe = null; }
  },
};
